import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { ClientDTO, PaginatedResponse, Role, RoleDetailsDTO, RoleRequestDTO, RoleUserDTO } from '../models/role.model';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class RoleService {

  private apiUrl = `${environment.apiUrl}/roles`;

  constructor(private http: HttpClient) { }

  /**
   * Récupère tous les rôles d'un utilisateur
   */
  getUserRoles(userId: string, clientId: string): Observable<PaginatedResponse<Role>> {
    let params = new HttpParams()
      .set('clientId', clientId.toString())

    return this.http.get<PaginatedResponse<Role>>(`${this.apiUrl}/${userId}?size=50`, { params })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Gestion des erreurs HTTP
   */
  private handleError(error: any): Observable<never> {
    console.error('Erreur dans RoleService:', error);

    let errorMessage = 'Une erreur est survenue lors de la gestion des rôles';

    if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.message) {
      errorMessage = error.message;
    } else if (error.status) {
      switch (error.status) {
        case 401:
          errorMessage = 'Non autorisé - Veuillez vous reconnecter';
          break;
        case 403:
          errorMessage = 'Accès refusé - Permissions insuffisantes';
          break;
        case 404:
          errorMessage = 'Ressource non trouvée';
          break;
        case 500:
          errorMessage = 'Erreur serveur interne';
          break;
        default:
          errorMessage = `Erreur ${error.status}: ${error.statusText}`;
      }
    }

    return throwError(() => new Error(errorMessage));
  }

  /**
   * Récupère les rôles client pour un client spécifique
   * @param clientId - L'ID du client sélectionné
   */
  getClientRoles(clientId?: string): Observable<PaginatedResponse<Role>> {
    const url = clientId
      ? `${this.apiUrl}/client-roles/${clientId}?size=50`
      : `${this.apiUrl}/client-roles?size=50`;

    return this.http.get<PaginatedResponse<Role>>(url)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Assigne un rôle client à un utilisateur pour un client spécifique
   * @param userId - L'ID de l'utilisateur
   * @param roleId - L'ID du rôle
   * @param clientId - L'ID du client
   */
  assignClientRole(userId: string, roleId: string, clientId: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/users/${userId}/client-roles/${roleId}/assign/${clientId}`, {})
      .pipe(catchError(this.handleError));
  }

  /**
   * Retire un rôle client d'un utilisateur pour un client spécifique
   * @param userId - L'ID de l'utilisateur
   * @param roleId - L'ID du rôle
   * @param clientId - L'ID du client
   */
  unassignClientRole(userId: string, roleId: string, clientId: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/users/${userId}/client-roles/${roleId}/unassign/${clientId}`)
      .pipe(catchError(this.handleError));
  }

  private getHeaders(): HttpHeaders {
    const token = localStorage.getItem('access_token');
    return new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });
  }

  // ===== RÉCUPÉRATION DES RÔLES =====
  /**
 * Récupère tous les rôles client avec pagination
 */
  getClientRolesV2(first: number = 0, max: number = 50, search?: string): Observable<PaginatedResponse<RoleUserDTO>> {
    let params = new HttpParams()
      .set('first', first.toString())
      .set('max', max.toString());

    if (search) {
      params = params.set('search', search);
    }

    return this.http.get<PaginatedResponse<RoleUserDTO>>(
      `${this.apiUrl}/client-roles`,
      { headers: this.getHeaders(), params }
    );
  }

  /**
   * Récupère les rôles d'un client spécifique
   */
  getClientRolesByClientId(clientId: string, first: number = 0, max: number = 50, search?: string): Observable<PaginatedResponse<RoleUserDTO>> {
    let params = new HttpParams()
      .set('first', first.toString())
      .set('max', max.toString());

    if (search) {
      params = params.set('search', search);
    }

    return this.http.get<PaginatedResponse<RoleUserDTO>>(
      `${this.apiUrl}/client-roles/${clientId}`,
      { headers: this.getHeaders(), params }
    );
  }

  /**
   * Récupère les détails d'un rôle realm
   */
  getRealmRoleDetails(roleName: string): Observable<RoleUserDTO> {
    return this.http.get<RoleUserDTO>(
      `${this.apiUrl}/realm/${roleName}`,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Récupère les détails d'un rôle client
   */
  getClientRoleDetails(clientId: string, roleName: string): Observable<RoleUserDTO> {
    return this.http.get<RoleUserDTO>(
      `${this.apiUrl}/client/${clientId}/${roleName}`,
      { headers: this.getHeaders() }
    );
  }

  // ===== CRÉATION DE RÔLES =====

  /**
   * Crée un rôle realm
   */
  createRealmRole(roleRequest: RoleRequestDTO): Observable<RoleUserDTO> {
    return this.http.post<RoleUserDTO>(
      `${this.apiUrl}/realm`,
      roleRequest,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Crée un rôle client
   */
  createClientRole(clientId: string, roleRequest: RoleRequestDTO): Observable<RoleUserDTO> {
    return this.http.post<RoleUserDTO>(
      `${this.apiUrl}/client/${clientId}`,
      roleRequest,
      { headers: this.getHeaders() }
    );
  }

  // ===== MISE À JOUR DE RÔLES =====

  /**
   * Met à jour un rôle realm
   */
  updateRealmRole(roleName: string, roleRequest: RoleRequestDTO): Observable<void> {
    return this.http.put<void>(
      `${this.apiUrl}/realm/${roleName}`,
      roleRequest,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Met à jour un rôle client
   */
  updateClientRole(clientId: string, roleName: string, roleRequest: RoleRequestDTO): Observable<void> {
    return this.http.put<void>(
      `${this.apiUrl}/client/${clientId}/${roleName}`,
      roleRequest,
      { headers: this.getHeaders() }
    );
  }

  // ===== SUPPRESSION DE RÔLES =====

  /**
   * Supprime un rôle realm
   */
  deleteRealmRole(roleName: string): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/realm/${roleName}`,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Supprime un rôle client
   */
  deleteClientRole(clientId: string, roleName: string): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/client/${clientId}/${roleName}`,
      { headers: this.getHeaders() }
    );
  }

  // ===== VÉRIFICATION D'EXISTENCE =====

  /**
   * Vérifie si un rôle existe
   */
  roleExists(roleName: string, clientId?: string): Observable<boolean> {
    let params = new HttpParams().set('roleName', roleName);

    if (clientId) {
      params = params.set('clientId', clientId);
    }

    return this.http.get<boolean>(
      `${this.apiUrl}/exists`,
      { headers: this.getHeaders(), params }
    );
  }

  // ===== RÉCUPÉRATION DES CLIENTS =====

  /**
   * Récupère la liste des clients (pour le formulaire)
   */
  getClients(): Observable<PaginatedResponse<ClientDTO>> {
    const token = localStorage.getItem('access_token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });

    return this.http.get<PaginatedResponse<ClientDTO>>(
      `${environment.apiUrl}/clients/all`,
      { headers }
    );
  }

  /**
 * Récupère tous les rôles realm avec pagination et option d'exclure les rôles par défaut
 */
  getAllRealmRoles(
    first: number = 0,
    max: number = 50,
    search?: string,
    excludeDefaultRoles: boolean = true
  ): Observable<PaginatedResponse<RoleUserDTO>> {
    let params = new HttpParams()
      .set('first', first.toString())
      .set('max', max.toString())
      .set('excludeDefaultRoles', excludeDefaultRoles.toString());

    if (search) {
      params = params.set('search', search);
    }

    return this.http.get<PaginatedResponse<RoleUserDTO>>(
      `${this.apiUrl}/realm-roles/all`,
      { headers: this.getHeaders(), params }
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
 * Récupère tous les rôles d'un groupe
 */
  getGroupRoles(groupId: string): Observable<any> {
    return this.http.get<any>(
      `${this.apiUrl}/groups/${groupId}`,
      { headers: this.getHeaders() }
    ).pipe(catchError(this.handleError));
  }

  /**
   * Attache des rôles à un groupe
   * Transformation en snake_case pour correspondre au backend
   */
  attachRolesToGroup(groupId: string, roleAssignment: any): Observable<void> {
    // Transformation de l'objet en snake_case
    const payload = {
      group_id: roleAssignment.groupId,
      role_ids: roleAssignment.roleIds,
      role_names: roleAssignment.roleNames,
      client_id: roleAssignment.clientId
    };

    // Nettoyage des propriétés undefined
    Object.keys(payload).forEach(key => {
      if (payload[key as keyof typeof payload] === undefined) {
        delete payload[key as keyof typeof payload];
      }
    });

    // console.log('📤 Payload envoyé au backend (snake_case):', payload);

    return this.http.post<void>(
      `${this.apiUrl}/groups/${groupId}/attach`,
      payload,
      { headers: this.getHeaders() }
    ).pipe(catchError(this.handleError));
  }

  /**
   * Détache des rôles d'un groupe
   * Transformation en snake_case pour correspondre au backend
   */
  detachRolesFromGroup(groupId: string, roleAssignment: any): Observable<void> {
    // Transformation de l'objet en snake_case
    const payload = {
      group_id: roleAssignment.groupId,
      role_ids: roleAssignment.roleIds,
      role_names: roleAssignment.roleNames,
      client_id: roleAssignment.clientId
    };

    // Nettoyage des propriétés undefined
    Object.keys(payload).forEach(key => {
      if (payload[key as keyof typeof payload] === undefined) {
        delete payload[key as keyof typeof payload];
      }
    });

    // console.log('📤 Payload envoyé au backend (snake_case):', payload);

    return this.http.delete<void>(
      `${this.apiUrl}/groups/${groupId}/detach`,
      {
        headers: this.getHeaders(),
        body: payload
      }
    ).pipe(catchError(this.handleError));
  }

  /**
 * Récupère pour un utilisateur la liste des applications (clients)
 * sur lesquelles il possède au moins un rôle, avec les détails de ces rôles.
 */
  getUserApplicationsWithRoles(userId: string): Observable<{ [appName: string]: { [roleName: string]: RoleDetailsDTO } }> {
    const token = localStorage.getItem('access_token');
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.get<{ [appName: string]: { [roleName: string]: RoleDetailsDTO } }>(
      `${this.apiUrl}/users/${userId}/applications-with-roles`,
      { headers }
    ).pipe(catchError(this.handleError));
  }
}